// ============================================================================
// REGION MASTER SCREEN — MSTR-RGN-001
// ============================================================================
// Introduces the cascading-FK pattern: parent (Zone) is loaded once and the
// resulting options drive (a) the form-modal Zone dropdown and (b) the grid's
// Zone filter.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ColumnSelector, type ColumnDefinition } from 'components/common';
import { ConfirmDialog, ModuleHeader } from 'components/modals';
import type { ActionButton } from 'components/modals';
import {
  ActionMenu,
  type ActionMenuItem,
  SyutGrid,
  type SyutGridColumn,
  SyutSelect,
  MinimizedModalStrip,
} from 'components/ui';
import notificationService from 'services/notificationService';
import { useModalMinimize } from 'contexts/ModalMinimizeContext';
import { useRegions } from 'hooks/api/useRegions';
import { useZones } from 'hooks/api/useZones';
import { regionService } from 'api/services/regionService';
import type { Region, RegionSearchRequest } from 'api/types/region.types';
import RegionFormModal, { type ZoneOption } from 'components/modals/modules/region-module/RegionFormModal';

type ViewMode = 'list' | 'grid';

interface RegionMasterScreenProps {
  /** Passed by main_layout when this component is rendered as a tabbed module. */
  tabId?: string;
  moduleName?: string;
}

const RegionMasterScreen: React.FC<RegionMasterScreenProps> = ({ tabId, moduleName }) => {
  const { isMinimized, dismiss: dismissMinimized, getMinimizedModal } = useModalMinimize();
  // ----- Server-side query params -----
  const [queryParams, setQueryParams] = useState<RegionSearchRequest>({
    page: 1,
    pageSize: 25,
  });
  const { data, loading, pagination, refetch } = useRegions(queryParams);

  // ----- Parent lookup: active zones for the form-modal dropdown -----
  // Form modal: only active zones can be selected when creating/editing regions.
  const { data: zones, refetch: refetchZones } = useZones({ page: 1, pageSize: 100 });

  // Separate lookup for the FILTER dropdown — all zones (active + inactive + deleted)
  // with status-suffixed labels so the user can filter regions by any zone state.
  const { data: allZones, refetch: refetchAllZones } = useZones({ page: 1, pageSize: 100, statusCode: 'ALL' });

  const zoneOptions: ZoneOption[] = useMemo(
    () => (zones ?? []).map(z => ({
      value: z.id,
      label: `${z.zoneCode} — ${z.zoneName}`,
    })),
    [zones]
  );

  const zoneFilterOptions = useMemo(() => {
    const all = (allZones ?? []).map(z => {
      let suffix = '';
      if (z.deletedAt) suffix = ' (Deleted)';
      else if (z.statusCode && z.statusCode !== 'ACTIVE') suffix = ` (${z.statusCode})`;
      return {
        value: z.id,
        label: `${z.zoneCode} — ${z.zoneName}${suffix}`,
      };
    });
    return [{ value: '', label: 'All zones' }, ...all];
  }, [allZones]);

  // ----- View state -----
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [searchQuery, setSearchQuery] = useState('');
  const [filterOpen, setFilterOpen] = useState(false);

  // ----- Filter panel state -----
  const [zoneFilter, setZoneFilter] = useState<string>('');
  const [regionCodeFilter, setRegionCodeFilter] = useState('');
  const [regionNameFilter, setRegionNameFilter] = useState('');
  // '' = default (server returns ACTIVE), 'INACTIVE' to view deactivated rows.
  const [statusFilter, setStatusFilter] = useState<string>('');

  // Debounced global search → server
  useEffect(() => {
    const t = setTimeout(() => {
      setQueryParams(prev => ({
        ...prev,
        searchTerm: searchQuery.trim() || undefined,
        page: 1,
      }));
    }, 400);
    return () => clearTimeout(t);
  }, [searchQuery]);

  // ----- Form modal state -----
  const [formModalOpen, setFormModalOpen] = useState(false);
  const [formMode, setFormMode] = useState<'create' | 'edit' | 'copy' | 'view'>('create');
  const [selectedRegion, setSelectedRegion] = useState<Region | null>(null);

  // Seed: if the region under edit has a parent zone that's no longer in the
  // active list (deactivated or deleted in another tab), we still need to render
  // its label so the user doesn't see a blank dropdown.
  //
  // The Region row carries denormalised zoneCode/zoneName from the API JOIN, so
  // we have the display text locally. To show the CORRECT state suffix (Inactive
  // vs Deleted), we look up the zone's actual state in `allZones` — already
  // loaded for the filter dropdown (statusCode: 'ALL' returns every state).
  const seedZoneOptions: ZoneOption[] = useMemo(() => {
    if (formMode !== 'edit' || !selectedRegion) return [];
    const r = selectedRegion;
    if (zoneOptions.some(o => o.value === r.zoneId)) return []; // already in active list

    // Derive the accurate suffix from the all-zones catalog.
    const parentFromAll = (allZones ?? []).find(z => z.id === r.zoneId);
    let suffix = ' (Inactive)'; // safe fallback if allZones hasn't loaded yet
    if (parentFromAll) {
      if (parentFromAll.deletedAt) suffix = ' (Deleted)';
      else if (parentFromAll.statusCode && parentFromAll.statusCode !== 'ACTIVE') {
        suffix = ` (${parentFromAll.statusCode})`;
      } else {
        // Shouldn't happen — parent is active but missing from active list.
        // Treat as no suffix.
        suffix = '';
      }
    }

    return [{
      value: r.zoneId,
      label: `${r.zoneCode ?? '?'} — ${r.zoneName ?? 'Unknown'}${suffix}`,
    }];
  }, [formMode, selectedRegion, zoneOptions, allZones]);

  // Minimize support: each Add gets a unique instance id so multiple Add modals can coexist
  // in the minimize bar; formModalMinimizeRef lets us programmatically minimize the
  // currently-open modal before opening a new one.
  const createInstanceCounter = useRef(0);
  const [createInstanceId, setCreateInstanceId] = useState<string>('0');
  const formModalMinimizeRef = useRef<(() => void) | null>(null);

  const handleRestoreModal = useCallback(
    (restoredMode: 'create' | 'edit' | 'copy' | 'view', instanceId?: string, recordCode?: string) => {
      // Refetch parent FK lookups first — a cross-tab change to Zone Master
      // (e.g. deactivating a zone) must reflect in this modal's dropdown.
      refetchZones();
      if (instanceId) setCreateInstanceId(instanceId);
      if (recordCode) {
        const rec = data.find((r) => r.regionCode === recordCode);
        if (rec) setSelectedRegion(rec);
      }
      setFormMode(restoredMode);
      setFormModalOpen(true);
    },
    [data, refetchZones]
  );

  // ----- Delete state -----
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [regionToDelete, setRegionToDelete] = useState<Region | null>(null);

  // ----- Handlers -----
  const handleAddNew = useCallback(() => {
    // Refetch parent FK lookups (Zone) so the dropdown reflects any cross-tab changes
    // since this screen was last visited. Single source of truth: the latest server state.
    refetchZones();
    // If a modal is currently open, auto-minimize it before opening this fresh Add.
    if (formModalOpen) {
      formModalMinimizeRef.current?.();
    }
    const newId = String(++createInstanceCounter.current);
    setCreateInstanceId(newId);
    setSelectedRegion(null);
    setFormMode('create');
    setFormModalOpen(true);
  }, [formModalOpen, refetchZones]);

  const handleEdit = useCallback(
    (region: Region) => {
      // Refetch parent FK lookups before opening edit modal (cross-tab freshness).
      refetchZones();
      // If this exact record is already minimized, restore it instead of opening fresh.
      const modalId = tabId ? `${tabId}-form-modal-edit-${region.regionCode}` : null;
      if (modalId && isMinimized(modalId)) {
        const modal = getMinimizedModal(modalId);
        dismissMinimized(modalId);
        modal?.onRestore?.();
        return;
      }
      setSelectedRegion(region);
      setFormMode('edit');
      setFormModalOpen(true);
    },
    [tabId, isMinimized, dismissMinimized, getMinimizedModal, refetchZones]
  );

  const handleView = useCallback(
    (region: Region) => {
      const modalId = tabId ? `${tabId}-view-modal-${region.regionCode}` : null;
      if (modalId && isMinimized(modalId)) {
        const modal = getMinimizedModal(modalId);
        dismissMinimized(modalId);
        modal?.onRestore?.();
        return;
      }
      setSelectedRegion(region);
      setFormMode('view');
      setFormModalOpen(true);
    },
    [tabId, isMinimized, dismissMinimized, getMinimizedModal],
  );

  const handleCopy = useCallback(
    (region: Region) => {
      setSelectedRegion(region);
      setFormMode('copy');
      setFormModalOpen(true);
    },
    [],
  );

  const handleDeleteClick = useCallback((region: Region) => {
    setRegionToDelete(region);
    setDeleteModalOpen(true);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (!regionToDelete) return;
    setIsDeleting(true);
    try {
      await regionService.delete(regionToDelete.id);
      setIsDeleting(false);
      setDeleteModalOpen(false);
      notificationService.toast.success(`Region "${regionToDelete.regionCode}" deleted successfully.`);
      setRegionToDelete(null);
      refetch();
    } catch (err: any) {
      setIsDeleting(false);
      const msg = err?.response?.data?.message || 'An error occurred while deleting.';
      notificationService.toast.error(msg, 6000);
    }
  }, [regionToDelete, refetch]);

  const handleSaved = useCallback(() => {
    refetch();
    refetchZones();     // active zones for form dropdown
    refetchAllZones();  // all zones for filter dropdown
  }, [refetch, refetchZones, refetchAllZones]);

  const handleRefresh = useCallback(() => {
    refetch();
    refetchZones();
    refetchAllZones();
  }, [refetch, refetchZones, refetchAllZones]);

  // Pagination handlers (0-based → 1-based)
  const handlePageChange = useCallback((pageIndex: number) => {
    setQueryParams(prev => ({ ...prev, page: pageIndex + 1 }));
  }, []);
  const handlePageSizeChange = useCallback((pageSize: number) => {
    setQueryParams(prev => ({ ...prev, pageSize, page: 1 }));
  }, []);

  // Filter Apply / Clear
  const handleApplyFilters = useCallback(() => {
    setQueryParams(prev => ({
      ...prev,
      zoneId: zoneFilter || undefined,
      regionCode: regionCodeFilter.trim() || undefined,
      regionName: regionNameFilter.trim() || undefined,
      statusCode: statusFilter || undefined,
      page: 1,
    }));
    setFilterOpen(false);
  }, [zoneFilter, regionCodeFilter, regionNameFilter, statusFilter]);

  const handleClearFilters = useCallback(() => {
    setZoneFilter('');
    setRegionCodeFilter('');
    setRegionNameFilter('');
    setStatusFilter('');
    setQueryParams({ page: 1, pageSize: queryParams.pageSize ?? 25 });
  }, [queryParams.pageSize]);

  // Active filter count
  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (queryParams.zoneId) count++;
    if (queryParams.regionCode) count++;
    if (queryParams.regionName) count++;
    if (queryParams.statusCode) count++;
    return count;
  }, [queryParams.zoneId, queryParams.regionCode, queryParams.regionName, queryParams.statusCode]);

  // ----- Grid columns -----
  const columns: SyutGridColumn<Region>[] = useMemo(() => [
    {
      id: 'zoneName',
      header: 'Zone',
      accessorKey: 'zoneName',
      size: 180,
      enableSorting: true,
      cell: ({ row }) => row.original.zoneName ?? row.original.zoneCode ?? '-',
    },
    {
      id: 'regionCode',
      header: 'Region Code',
      accessorKey: 'regionCode',
      size: 160,
      enableSorting: true,
    },
    {
      id: 'regionName',
      header: 'Region Name',
      accessorKey: 'regionName',
      size: 280,
      enableSorting: true,
      flex: true,
    },
    {
      id: 'statusCode',
      header: 'Status',
      accessorKey: 'statusCode',
      size: 110,
      enableSorting: true,
      cell: ({ row }) => {
        const r = row.original;
        // Deleted-state takes precedence over status_id label.
        const code = r.deletedAt ? 'DELETED' : (r.statusCode || 'ACTIVE');
        let color = '#6b7280'; // grey for INACTIVE / unknown
        if (code === 'ACTIVE') color = '#16a34a';   // green
        else if (code === 'DELETED') color = '#dc2626'; // red
        return (
          <span
            style={{
              fontSize: '0.75rem',
              fontWeight: 600,
              color,
              padding: '2px 8px',
              border: `1px solid ${color}`,
              borderRadius: '999px',
            }}
            title={r.deletedAt ? `Deleted at ${new Date(r.deletedAt).toLocaleString()}` : undefined}
          >
            {code}
          </span>
        );
      },
    },
    {
      id: 'updatedAt',
      header: 'Last Updated',
      accessorKey: 'updatedAt',
      size: 150,
      enableSorting: true,
      cell: ({ row }) => {
        const v = row.original.updatedAt;
        if (!v) return '-';
        try {
          return new Date(v).toLocaleString();
        } catch {
          return v;
        }
      },
    },
  ], []);

  // Column visibility (driven by ColumnSelector toolbar control)
  const columnDefinitions: ColumnDefinition[] = useMemo(
    () =>
      columns.map((col) => ({
        id: col.id as string,
        label: typeof col.header === 'string' ? (col.header as string) : (col.id as string),
        defaultVisible: true,
      })),
    [columns],
  );
  const [visibleColumns, setVisibleColumns] = useState<string[]>(() =>
    columnDefinitions.filter((c) => c.defaultVisible).map((c) => c.id),
  );
  const handleApplyColumns = useCallback((cols: string[]) => setVisibleColumns(cols), []);
  const handleResetColumns = useCallback(() => {
    setVisibleColumns(columnDefinitions.filter((c) => c.defaultVisible).map((c) => c.id));
  }, [columnDefinitions]);

  // ----- Row actions -----
  const getRowActions = useCallback((region: Region): ActionMenuItem[] => [
    { id: 'view', label: 'View Details', icon: <IconSystem.eye size={16} />, onClick: () => handleView(region) },
    { id: 'edit',
      label: 'Edit',
      icon: <IconSystem.edit size={16} />,
      onClick: () => handleEdit(region),
    },
    { id: 'copy', label: 'Copy', icon: <IconSystem.copy size={16} />, onClick: () => handleCopy(region) },
    { id: 'delete',
      label: 'Delete',
      icon: <IconSystem.trash size={16} />,
      onClick: () => handleDeleteClick(region),
      variant: 'danger',
    },
  ], [handleEdit, handleView, handleCopy, handleDeleteClick]);

  // ----- Header action buttons -----
  const actionButtons: ActionButton[] = useMemo(() => [
    {
      id: 'add',
      icon: <IconSystem.add size={18} />,
      label: 'Add',
      tooltip: 'Add New Region',
      onClick: handleAddNew,
      variant: 'icon',
    },
    {
      id: 'refresh',
      icon: <IconSystem.refresh size={18} />,
      label: 'Refresh',
      tooltip: 'Refresh',
      onClick: handleRefresh,
      variant: 'icon',
    },
  ], [handleAddNew, handleRefresh]);

  return (
    <div className="module-master-container">
      <ModuleHeader
        title="Region Master"
        description="Second level of the branch reporting hierarchy. Each region rolls up into a zone."
        icon={<IconSystem.folder size={18} />}
        searchConfig={{
          value: searchQuery,
          onChange: setSearchQuery,
          placeholder: 'Search code, name, zone...',
        }}
        viewModeConfig={{
          currentMode: viewMode,
          modes: [
            { id: 'list', icon: <IconSystem.list size={16} />, label: 'List', tooltip: 'List view' },
            { id: 'grid', icon: <IconSystem.grid size={16} />, label: 'Grid', tooltip: 'Grid view' },
          ],
          onChange: (mode) => setViewMode(mode as ViewMode),
        }}
        filterConfig={{
          onClick: () => setFilterOpen(!filterOpen),
          isActive: filterOpen || activeFilterCount > 0,
          activeFilterCount,
        }}
        leadingToolbarItems={tabId ? <MinimizedModalStrip tabId={tabId} /> : undefined}
        customToolbarItems={
          <ColumnSelector
            columns={columnDefinitions}
                visibleColumns={visibleColumns}
            onApply={handleApplyColumns}
            onReset={handleResetColumns}
          />
        }
        actions={actionButtons}
      />

      <div className="module-content" style={{ marginTop: '6px' }}>
        <div style={{ display: 'flex', gap: '0', flex: 1, overflow: 'hidden' }}>
          <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
            {viewMode === 'list' && (
              <SyutGrid
                data={data}
                columns={columns}
                enableSorting
                enablePagination
                manualPagination
                totalCount={pagination.totalItems}
                currentPage={(queryParams.page ?? 1) - 1}
                pageSize={queryParams.pageSize ?? 25}
                onPageChange={handlePageChange}
                onPageSizeChange={handlePageSizeChange}
              visibleColumns={visibleColumns}
                rowActions={getRowActions}
                isLoading={loading}
                emptyMessage="No regions found"
              />
            )}

            {viewMode === 'grid' && (
              <div
                className="syut-grid-component"
                style={{ height: '100%', display: 'flex', flexDirection: 'column' }}
              >
                <div
                  style={{
                    padding: '1rem',
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                    gap: '1rem',
                    flex: 1,
                    overflow: 'auto',
                    alignContent: 'start',
                  }}
                >
                  {data.map((r) => (
                    <div
                      key={String(r.id)}
                      style={{
                        position: 'relative',
                        border: '1px solid var(--border-color)',
                        borderRadius: '8px',
                        padding: '1rem',
                        background: 'var(--bg-primary)',
                        cursor: 'pointer',
                      }}
                      onClick={() => handleEdit(r)}
                    >
                      {/* Action menu pinned to the card's top-right; stop click
                          propagation so opening the menu doesn't also fire the
                          card's onClick. */}
                      <div
                        style={{ position: 'absolute', top: 6, right: 6 }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <ActionMenu items={getRowActions(r)} />
                      </div>
                      <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', fontWeight: 600, paddingRight: '1.5rem' }}>
                        {r.zoneName ?? r.zoneCode} · {r.regionCode}
                      </div>
                      <div style={{ fontSize: '1rem', fontWeight: 600, marginTop: '0.25rem', color: 'var(--text-primary)' }}>
                        {r.regionName}
                      </div>
                      <div style={{ fontSize: '0.75rem', marginTop: '0.5rem', color: r.statusCode === 'ACTIVE' ? '#16a34a' : '#6b7280' }}>
                        {r.statusCode || 'ACTIVE'}
                      </div>
                    </div>
                  ))}
                </div>
                {data.length > 0 && (
                  <div className="pagination-footer">
                    <div className="pagination-info">
                      Showing {data.length} of {pagination.totalItems} items
                    </div>
                    <div className="pagination-controls" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <select
                        value={queryParams.pageSize ?? 25}
                        onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                        style={{ padding: '0.375rem 0.5rem', borderRadius: '0.375rem', border: '1px solid var(--border-color)', fontSize: '0.85rem', marginRight: '0.5rem' }}
                      >
                        {[10, 25, 50, 100].map((size) => (
                          <option key={size} value={size}>{size} / page</option>
                        ))}
                      </select>
                      <button className="page-btn" onClick={() => handlePageChange(0)} disabled={(queryParams.page ?? 1) <= 1}>
                        <IconSystem.chevronLeft size={16} />
                        <IconSystem.chevronLeft size={16} style={{ marginLeft: '-8px' }} />
                      </button>
                      <button className="page-btn" onClick={() => handlePageChange((queryParams.page ?? 1) - 2)} disabled={(queryParams.page ?? 1) <= 1}>
                        <IconSystem.chevronLeft size={16} />
                      </button>
                      <span style={{ padding: '0 0.5rem', fontSize: '0.85rem' }}>
                        Page {queryParams.page ?? 1} of {Math.max(1, Math.ceil(pagination.totalItems / (queryParams.pageSize ?? 25)))}
                      </span>
                      <button className="page-btn" onClick={() => handlePageChange(queryParams.page ?? 1)} disabled={(queryParams.page ?? 1) >= Math.max(1, Math.ceil(pagination.totalItems / (queryParams.pageSize ?? 25)))}>
                        <IconSystem.chevronRight size={16} />
                      </button>
                      <button className="page-btn" onClick={() => handlePageChange(Math.max(1, Math.ceil(pagination.totalItems / (queryParams.pageSize ?? 25))) - 1)} disabled={(queryParams.page ?? 1) >= Math.max(1, Math.ceil(pagination.totalItems / (queryParams.pageSize ?? 25)))}>
                        <IconSystem.chevronRight size={16} />
                        <IconSystem.chevronRight size={16} style={{ marginLeft: '-8px' }} />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Filter panel — conditional render (prima pattern) */}
        {filterOpen && (
        <div className="search-panel">
          <div className="search-panel-header">
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
              <IconSystem.filter size={14} />
              <h3 className="search-panel-title">Filters</h3>
            </div>
            <button
              onClick={() => { handleClearFilters(); setFilterOpen(false); }}
              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0.25rem', display: 'flex', alignItems: 'center', color: 'var(--text-muted)', borderRadius: '0.25rem' }}
              title="Close filters"
            >
              <IconSystem.x size={16} />
            </button>
          </div>
          <div className="search-panel-content">
            <div className="filter-section">
              <SyutSelect
                label="Zone"
                options={zoneFilterOptions}
                value={zoneFilter}
                onChange={(v) => setZoneFilter((v as string) || '')}
                isClearable={false}
                size="sm"
              />
            </div>

            <div className="filter-section">
              <label className="form-label" style={{ fontSize: '0.8125rem', marginBottom: '0.25rem' }}>
                Region Code
              </label>
              <input
                type="text"
                className="form-input"
                value={regionCodeFilter}
                onChange={(e) => setRegionCodeFilter(e.target.value)}
                placeholder="Filter by code..."
                style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }}
              />
            </div>

            <div className="filter-section">
              <label className="form-label" style={{ fontSize: '0.8125rem', marginBottom: '0.25rem' }}>
                Region Name
              </label>
              <input
                type="text"
                className="form-input"
                value={regionNameFilter}
                onChange={(e) => setRegionNameFilter(e.target.value)}
                placeholder="Filter by name..."
                style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }}
              />
            </div>

            <div className="filter-section">
              <SyutSelect
                label="Status"
                options={[
                  { value: '', label: 'Active only (default)' },
                  { value: 'INACTIVE', label: 'Inactive only' },
                  { value: 'DELETED', label: 'Deleted only' },
                  { value: 'ALL', label: 'All (incl. deleted)' },
                ]}
                value={statusFilter}
                onChange={(v) => setStatusFilter((v as string) || '')}
                isClearable={false}
                size="sm"
              />
            </div>
          </div>
          <div className="filter-actions">
            <button className="btn btn-secondary" onClick={handleClearFilters}>
              Clear
            </button>
            <button className="btn btn-primary" onClick={handleApplyFilters}>
              Apply
            </button>
          </div>
        </div>
        )}
      </div>

      <ConfirmDialog
        open={deleteModalOpen}
        title="Confirm Delete"
        message={
          regionToDelete
            ? `Are you sure you want to delete region "${regionToDelete.regionCode}"? This action cannot be undone.`
            : ''
        }
        confirmLabel="Delete"
        cancelLabel="Cancel"
        variant="danger"
        loading={isDeleting}
        loadingMessage="Deleting..."
        onConfirm={handleConfirmDelete}
        onCancel={() => {
          if (!isDeleting) {
            setDeleteModalOpen(false);
            setRegionToDelete(null);
          }
        }}
      />

      <RegionFormModal
        isOpen={formModalOpen}
        mode={formMode}
        record={selectedRegion}
        zoneOptions={zoneOptions}
        seedZoneOptions={seedZoneOptions}
        onSaved={handleSaved}
        onClose={() => setFormModalOpen(false)}
        tabId={tabId}
        moduleName={moduleName}
        onRestoreModal={handleRestoreModal}
        createInstanceId={createInstanceId}
        minimizeRef={formModalMinimizeRef}
      />
    </div>
  );
};

export default RegionMasterScreen;
